import React, { useMemo, useState, useEffect } from 'react';
import { Input, Segmented, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import ApprovalTrack from './ApprovalTrack';
import FestivalFilter from './FestivalFilter';
import StatusBadge from './StatusBadge';
import {
  formatDate,
  formatEuro,
  getInitiative,
  getOrderGroup,
  getReference,
} from '../../utils/directOrderStatus';

const GROUPS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const SORTS = [
  { value: 'newest', label: 'Newest first', fn: (a, b) => b.doId - a.doId },
  { value: 'oldest', label: 'Oldest first', fn: (a, b) => a.doId - b.doId },
  { value: 'value', label: 'Highest value', fn: (a, b) => parseFloat(b.doTotalCost || 0) - parseFloat(a.doTotalCost || 0) },
  { value: 'supplier', label: 'Supplier A to Z', fn: (a, b) => (a.doSupplier || '').localeCompare(b.doSupplier || '') },
];

const PAGE_SIZE = 20;

const total = (orders) => orders.reduce((sum, o) => sum + (parseFloat(o.doTotalCost) || 0), 0);

const matchesSearch = (order, term) => {
  if (!term) return true;
  const haystack = [
    order.doId,
    order.doRef,
    order.doSupplier,
    order.doNameBriefDesc,
    order.doOfficerNameFull,
    order.doEntityName,
    getInitiative(order),
  ]
    .filter((v) => v !== null && v !== undefined)
    .join(' ')
    .toLowerCase();
  return haystack.includes(term);
};

/** One order as a ruled row; the whole row opens the order. */
const OrderRow = ({ order, onOpen, renderActions, showSubmitter }) => {
  const open = () => onOpen && onOpen(order);
  return (
    <li
      className={`do-entry${renderActions ? ' do-entry--actions' : ''}`}
      onClick={open}
    >
      <span className="do-entry__ref">
        <span className="do-ref">{getReference(order)}</span>
        <span className="do-entry__date">{formatDate(order.doDate)}</span>
      </span>
      <span className="do-entry__main">
        <button
          type="button"
          className="do-entry__supplier"
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
        >
          {order.doSupplier || 'Unknown supplier'}
        </button>
        {(showSubmitter || order.doNameBriefDesc) && (
          <span className="do-entry__desc">
            {showSubmitter && (
              <span className="do-entry__who">
                {order.doOfficerNameFull || order.doEmailName || order.doEmailAddress || 'Unknown'}
              </span>
            )}
            {showSubmitter && order.doNameBriefDesc && ' · '}
            {order.doNameBriefDesc}
          </span>
        )}
      </span>
      <span className="do-entry__amt do-num">{formatEuro(order.doTotalCost)}</span>
      <span className="do-entry__state">
        <StatusBadge order={order} />
        <ApprovalTrack order={order} />
      </span>
      {renderActions && (
        <span className="do-entry__actions" onClick={(e) => e.stopPropagation()}>
          {renderActions(order)}
        </span>
      )}
    </li>
  );
};

/**
 * Direct orders laid out like a festival programme: the festival filter on top
 * (busiest festivals plus a searchable list of all of them), then orders grouped
 * under each festival with its count and total value. Picking a festival (or
 * searching) shows one flat list instead.
 *
 * compact: no toolbar and at most five orders per festival (overview pages)
 */
const OrdersExplorer = ({
  orders = [],
  loading = false,
  onOpen,
  renderActions,
  showSubmitter = false,
  hideGroupFilter = false,
  hideInitiativeFilter = false,
  toolbarExtra = null,
  emptyTitle = 'No direct orders',
  emptyHint = 'Orders will show up here once they are raised.',
  resetKey,
  initialGroup = 'all',
  initialInitiative = 'all',
  compact = false,
}) => {
  const [group, setGroup] = useState(initialGroup);
  const [search, setSearch] = useState('');
  const [initiative, setInitiative] = useState(initialInitiative);
  const [sort, setSort] = useState('newest');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Reset filters when the caller switches to a different data set.
  useEffect(() => {
    setGroup(initialGroup);
    setSearch('');
    setInitiative(initialInitiative);
    setVisibleCount(PAGE_SIZE);
  }, [resetKey, initialGroup, initialInitiative]);

  const counts = useMemo(() => {
    const result = { all: orders.length, pending: 0, approved: 0, rejected: 0 };
    orders.forEach((order) => {
      result[getOrderGroup(order)] += 1;
    });
    return result;
  }, [orders]);

  const festivals = useMemo(() => {
    const byName = {};
    orders.forEach((order) => {
      const name = getInitiative(order);
      if (!byName[name]) byName[name] = { count: 0, total: 0 };
      byName[name].count += 1;
      byName[name].total += parseFloat(order.doTotalCost) || 0;
    });
    return Object.keys(byName)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ name, count: byName[name].count, total: byName[name].total }));
  }, [orders]);

  const term = search.trim().toLowerCase();
  const sortFn = (SORTS.find((s) => s.value === sort) || SORTS[0]).fn;

  const filtered = useMemo(
    () =>
      orders
        .filter(
          (order) =>
            (hideGroupFilter || group === 'all' || getOrderGroup(order) === group) &&
            (hideInitiativeFilter || initiative === 'all' || getInitiative(order) === initiative) &&
            matchesSearch(order, term)
        )
        .sort(sortFn),
    [orders, group, initiative, term, hideGroupFilter, hideInitiativeFilter, sortFn]
  );

  // Group by festival when showing every festival and not searching.
  const grouped = !hideInitiativeFilter && initiative === 'all' && !term && festivals.length > 1;
  const sections = useMemo(() => {
    if (!grouped) return [];
    const map = new Map();
    filtered.forEach((order) => {
      const name = getInitiative(order);
      if (!map.has(name)) map.set(name, []);
      map.get(name).push(order);
    });
    // biggest festival first
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  }, [grouped, filtered]);

  const perSection = compact ? 5 : 8;

  const rows = (list) => (
    <ul className="do-entries">
      {list.map((order) => (
        <OrderRow
          key={order.doId}
          order={order}
          onOpen={onOpen}
          renderActions={renderActions}
          showSubmitter={showSubmitter}
        />
      ))}
    </ul>
  );

  const hasData = orders.length > 0;
  let body;
  if (loading && !hasData) {
    body = <div className="do-empty">Loading orders…</div>;
  } else if (filtered.length === 0) {
    body = (
      <div className="do-empty">
        <strong>{hasData ? 'No matching orders' : emptyTitle}</strong>
        {hasData ? 'Try a different search or filter.' : emptyHint}
      </div>
    );
  } else if (grouped) {
    body = sections.map(([name, list]) => (
      <section className="do-programme" key={name} aria-label={name}>
        <header className="do-programme__head">
          <h3 className="do-programme__name">{name}</h3>
          <span className="do-programme__total">
            {list.length} {list.length === 1 ? 'order' : 'orders'} ·{' '}
            <strong className="do-num">{formatEuro(total(list))}</strong>
          </span>
        </header>
        {rows(list.slice(0, perSection))}
        {list.length > perSection && (
          <button type="button" className="do-link do-programme__more" onClick={() => setInitiative(name)}>
            Show all {list.length} in {name}
          </button>
        )}
      </section>
    ));
  } else {
    body = (
      <>
        {initiative !== 'all' && !hideInitiativeFilter && (
          <header className="do-programme__head do-programme__head--single">
            <h3 className="do-programme__name">{initiative}</h3>
            <span className="do-programme__total">
              {filtered.length} {filtered.length === 1 ? 'order' : 'orders'} ·{' '}
              <strong className="do-num">{formatEuro(total(filtered))}</strong>
            </span>
          </header>
        )}
        {rows(filtered.slice(0, visibleCount))}
        {filtered.length > visibleCount && (
          <button
            type="button"
            className="do-link do-programme__more"
            onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
          >
            Show {Math.min(PAGE_SIZE, filtered.length - visibleCount)} more of {filtered.length - visibleCount}
          </button>
        )}
      </>
    );
  }

  return (
    <div className="do-explorer">
      {!hideInitiativeFilter && festivals.length > 1 && (
        <FestivalFilter
          festivals={festivals}
          value={initiative}
          totalCount={orders.length}
          onChange={(key) => {
            setInitiative(key);
            setVisibleCount(PAGE_SIZE);
          }}
        />
      )}
      {!compact && (
        <div className="do-toolbar">
          {!hideGroupFilter && (
            <Segmented
              value={group}
              onChange={(value) => {
                setGroup(value);
                setVisibleCount(PAGE_SIZE);
              }}
              options={GROUPS.map((g) => ({ value: g.value, label: `${g.label} ${counts[g.value]}` }))}
            />
          )}
          {toolbarExtra}
          <div className="do-toolbar__grow" />
          <Select value={sort} onChange={setSort} options={SORTS} style={{ width: 160 }} aria-label="Sort orders" />
          <Input
            className="do-search"
            allowClear
            prefix={<SearchOutlined aria-hidden="true" />}
            placeholder="Search orders"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setVisibleCount(PAGE_SIZE);
            }}
            aria-label="Search direct orders"
          />
        </div>
      )}
      {body}
    </div>
  );
};

export default OrdersExplorer;
